/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify,
 * merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthConfigurationService } from './../auth/auth-configuration.service';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SupplierService } from '../suppliers/supplier.service';
import { TenantConfigService } from '../tenant-config/tenant-config.service';
import { TenantEntitlementService } from '../auth/tenant-entitlement.service';
import { applyAppLanguage, langFromUrlQuery } from '../../language-bootstrap';

import { Auth } from 'aws-amplify';


@Component({
  selector: 'app-unauthorized',
  templateUrl: './unauthorized.component.html',
  styleUrls: ['./unauthorized.component.scss'],
})
export class UnauthorizedComponent implements OnInit {
  tenantForm: FormGroup;
  params$: Observable<void>;
  error = false;
  errorMessage: string;


  // indication if to navigate automatically to a tenent that was already included in cache 
  // 
  navigateAutomatically: boolean = true

  constructor(
    private fb: FormBuilder,
    private authConfigService: AuthConfigurationService,
    private _snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private supplierService: SupplierService,
    private tenantConfigService: TenantConfigService,
    private tenantEntitlements: TenantEntitlementService
  ) {}

  ngOnInit(): void {
    // Re-apply ?lang= if present on this route (hash query) or still on the page URL.
    const routeLang = (this.route.snapshot.queryParamMap.get('lang') || '').trim().toLowerCase();
    const urlLang = langFromUrlQuery();
    const lang = routeLang === 'en' || routeLang === 'he' ? routeLang : urlLang;
    if (lang) {
      applyAppLanguage(this.translateService, lang);
    }

    this.tenantForm = this.fb.group({
      tenantName: [null, [Validators.required]],
    });


    // just a thought.... 
    // since users might have a login problem (some old caching prevent login to cognito)
    // we might do not want to allow an automatic connection to an existing tenent, 
    // but just to pre-populate name... ???
    // 
    if (localStorage.getItem('tenantName')) {

      this.tenantForm.patchValue({tenantName:localStorage.getItem('tenantName')})

      // Logo on Amplify sign-in sent the user here on purpose; do not auto-continue.
      if (sessionStorage.getItem('stayOnTenantSelection')) {
        sessionStorage.removeItem('stayOnTenantSelection');
        this.navigateAutomatically = false;
        return;
      }

      // time to delay automatic navigation 
      var timeToWait = 4 * 1000

      const tenantName = localStorage.getItem('tenantName');
      const translated = this.translateService.instant('ERROR.NAVIGATION_TO', { tenant: tenantName });
      var snackBarRef = this._snackBar.open(
        translated,
        this.translateService.instant('ERROR.CANCEL'),
        { duration: timeToWait }
      );
      
      snackBarRef.onAction().subscribe(() => {
        //console.log('The snackbar action was triggered!');
        this.navigateAutomatically = false 

        // user clicked on cancel automatice navigation. let's clean tenant details 
        // 
        localStorage.setItem('tenantName',"") 
        
        // in case user was still logged in, we do not want system to login automatically to 
        // this user, after a new tenent name is typed. 
        // 
        this.logout()

      });
      

      // trigger a delay autumatic navigation, so user get the chance to dismiss it 
      setTimeout(()=>{ 
        this.navigateAutomaticallyAction()
      }, timeToWait) // timeout structure      

       
    }
    
  }

  navigateAutomaticallyAction () { 
      if (this.navigateAutomatically) { 
        //this.router.navigate(['/dashboard']);
        this.router.navigateByUrl(this.tenantEntitlements.homeUrl());
        //this.router.navigate(['products', 'import']);
        //this.router.navigate(['/products/dashboard-list']);
        //this.router.navigate(['/products']);
        //this.router.navigate(['/products/list']);
        //this.router.navigate(['/']);
      }

  }

  isFieldInvalid(field: string) {
    const formField = this.tenantForm.get(field);
    return (
      formField && formField.invalid && (formField.dirty || formField.touched)
    );
  }

  displayFieldCss(field: string) {
    return {
      'is-invalid': this.isFieldInvalid(field),
    };
  }

  hasRequiredError(field: string) {
    return !!this.tenantForm.get(field)?.hasError('required');
  }

  openErrorMessageSnackBar(errorMessage: string) {
    this._snackBar.open(errorMessage, 'Dismiss', {
      duration: 4 * 1000, // seconds
    });
  }

  login() {
    let tenantName = this.tenantForm.value.tenantName;
    if (!tenantName) {
      this.translateService.get('ERROR.NO_TENANT_NAME').subscribe((res: string) => {
        this.errorMessage = res;
        this.error = true;
        this.openErrorMessageSnackBar(this.errorMessage);
      });
      return false;
    }

    this.authConfigService
      .setTenantConfig(tenantName)
      .then((val) => {
        //this.router.navigate(['/dashboard']);
        this.router.navigateByUrl(this.tenantEntitlements.homeUrl());
        //this.router.navigate(['products', 'import']);
        //this.router.navigate(['/products/dashboard-list']);
        //this.router.navigate(['/products']);
        //this.router.navigate(['/products/list']);
        //this.router.navigate(['/']);
        //this.router.navigate(['/']);

      })
      .catch((errorResponse) => {
        this.error = true;
        this.errorMessage =
          errorResponse.error.message || this.translateService.instant('ERROR.UNEXPECTED');
        this.openErrorMessageSnackBar(this.errorMessage);
      });

    return false;
  }

  // in cancel automatic navigation - in case there was a user logged in, 
  // so we want to avoid system autoamtically connecting to an active user session 
  // 
  async logout() {

    console.log("logout")

    this.supplierService.clearSupplierCache();
    this.tenantConfigService.clearTenantConfigCache();
    await Auth.signOut()
      .then((e) => {
        this.authConfigService.cleanLocalStorage();

      })
      .catch((err) => {
        console.error('Error logging out: ', err);
      });

    console.log("After Logout!")

    // since for some reason logout is not succesfull - localStorage congnito related keys are not cleaned. 
    // so as a work-around we clear it directly. 
    // TODO: yes - this is not a good solution. but to be fixed in future.
    // 
    Object.keys(localStorage)
    .filter(key => key.includes('CognitoIdentityServiceProvider'))
    .forEach(key => localStorage.removeItem(key));
  
    //this.router.navigate(['/unauthorized']);
    window.location.reload(); 

  }

  switchLanguage(lang: string) {
    localStorage.setItem('userLang', lang);
    this.translateService.use(lang);
    document.documentElement.lang = lang;
    if (lang === 'he') {
      document.documentElement.dir = 'rtl';
      localStorage.setItem('direction', 'rtl');
    } else {
      document.documentElement.dir = 'ltr';
      localStorage.setItem('direction', 'ltr');
    }
  }

}
